import { Wrench, Zap, Hammer, HardHat, ClipboardList, Calculator, Ruler, Paintbrush, Home, Trees, FolderKanban, MessageCircle, ListTodo, BellRing, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const TRADES = [
  [Wrench, "Plumbers"],
  [Zap, "Electricians"],
  [Hammer, "General builders"],
  [HardHat, "Contractors"],
  [ClipboardList, "Project managers"],
  [Calculator, "Cost estimators"],
  [Ruler, "Surveyors"],
  [Paintbrush, "Decorators"],
  [Home, "Roofers"],
  [Trees, "Landscapers"],
];

const USES = [
  [FolderKanban, "Multiple projects", "Run every job side by side — each one stays organised on its own."],
  [MessageCircle, "Customer communication", "Customers simply message you back. No portals, no logins for them."],
  [ListTodo, "Internal staff tasks", "Turn any message into a task for your team, inside the same conversation."],
  [BellRing, "Daily digest", "Start the day with a summary of every incomplete task across your projects."],
  [BadgeCheck, "Approvals", "Arrange approvals in the chat and keep a record of who said yes — and when."],
  [Calculator, "Estimates & surveys", "Cost estimates, measurements and survey notes live where the conversation happened."],
];

export const Trades = () => (
  <section id="trades" className="bg-white py-28 sm:py-36" data-testid="trades-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="02" label="For every trade" />
      <div className="mt-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal delay={0.1}>
            <SectionHeading>The project messaging app for every trade.</SectionHeading>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg leading-relaxed text-diem-slate max-w-lg">
              If your work runs through messages, Deyem keeps it organised — quotes, measurements,
              approvals and deadlines included.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="trades-chips">
              {TRADES.map(([Icon, t]) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full bg-diem-paper border border-diem-line px-4 py-2 text-sm font-semibold text-diem-slate hover:border-diem-blue/40 hover:text-diem-blue transition-colors duration-300 cursor-default">
                  <Icon className="w-4 h-4 text-diem-blue" /> {t}
                </span>
              ))}
              <span className="inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-diem-muted cursor-default">
                …and everyone in between
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <div className="rounded-3xl bg-white border border-diem-line shadow-[0_24px_60px_-16px_rgba(15,23,42,0.15)] p-6" data-testid="daily-digest-demo">
            <motion.div
              initial={{ opacity: 0, y: -14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl bg-diem-ink p-4 flex items-start gap-3.5"
            >
              <img src="/icon.png" alt="Deyem" className="w-10 h-10 rounded-xl shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[13px] font-bold text-white">Daily Digest</p>
                  <p className="text-[11px] text-white/40 font-medium shrink-0">9:00</p>
                </div>
                <p className="mt-1 text-[13px] text-white/70 leading-snug">
                  3 incomplete tasks across 2 projects. 2 approvals waiting.
                </p>
              </div>
            </motion.div>

            <div className="mt-4 space-y-2.5">
              {[
                ["Oakfield Drive", "Book electrician", "Due Tuesday", "text-diem-warn bg-diem-warn/10"],
                ["Riverside Loft", "Send cost estimate", "Overdue", "text-diem-err bg-diem-err/10"],
                ["Oakfield Drive", "Confirm survey notes", "Due Friday", "text-diem-warn bg-diem-warn/10"],
              ].map(([proj, task, due, tone], i) => (
                <motion.div
                  key={task}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.55 + i * 0.12, duration: 0.5 }}
                  className="flex items-center gap-3 rounded-xl border border-diem-line bg-diem-paper px-4 py-3"
                >
                  <span className="w-[18px] h-[18px] rounded-md border-2 border-diem-line shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-diem-ink truncate">{task}</p>
                    <p className="text-[11px] text-diem-muted font-medium">{proj}</p>
                  </div>
                  <span className={`text-[10px] font-bold rounded-full px-2.5 py-1 shrink-0 ${tone}`}>{due}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[12px] text-diem-muted font-medium text-center">
              One message every morning. Nothing quietly slips.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {USES.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={0.08 + i * 0.06} y={26}>
            <div className="rounded-3xl border border-diem-line bg-white p-7 h-full hover:border-diem-blue/40 hover:shadow-[0_16px_44px_-12px_rgba(0,85,255,0.15)] transition-[border-color,box-shadow] duration-300" data-testid={`trade-use-${t.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
              <span className="w-11 h-11 rounded-2xl bg-diem-blue/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-diem-blue" />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-diem-ink">{t}</h3>
              <p className="mt-2.5 text-[14px] text-diem-slate leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
